import { IOptionsWithTypes, Room, SectionId } from "@eventr/common";
import { Box, Stack } from "@mui/material";
import { cardTranslation } from "i18n/translateHelpers";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { routes } from "routing";
import useDerivedScreenType from "v2/hooks/useDerivedScreenType";
import { useObjectContext } from "../providers/objectProvider";
import { CardText } from "../subComponents/cardText";
import { ChatButton } from "../subComponents/chatButton";
import { EditButton } from "../subComponents/editButton";
import { ExpandableSection } from "../subComponents/expendableSection";
import { HotelCheckInTimes } from "./checkInCheckOut";
import { HotelAttachments } from "./hotelAttachments";
import { HotelGuests } from "./hotelGuests";
import { HotelRoomInfo } from "./hotelRoomInfo";
import { ProviderButton } from "./providerButton";
import { CategoryList } from "../subComponents/categoryList";

interface IHotelBookingInfoProps {
  room?: Room;
  attachments?: {
    notes?: string;
    extra_docs?: string;
    website?: string;
    phoneNumber?: string;
    contactName?: string;
  };
  rooms?: Room[];
  chatNames: string[];
  providerData?: any;
  variant?: "expandable" | "static";
  categories?: string[];
  /** Leaves the guest's chat to the page, which carries it elsewhere. */
  joinOnly?: boolean;
}

export const HotelBookingInfo = ({
  room,
  attachments,
  rooms,
  chatNames,
  providerData,
  variant = "expandable",
  categories,
  joinOnly,
}: IHotelBookingInfoProps) => {
  const screenType = useDerivedScreenType();
  const { _id, sectionID } = useObjectContext();
  const { itemId: idFromParams = "" } = useParams();

  const mid = useMemo(() => <CardText>{cardTranslation("Booking Information")}</CardText>, []);

  const allGuests = useMemo(() => {
    const all: IOptionsWithTypes[] = [];
    rooms?.forEach((room) => {
      all.push(...(room?.guests ?? []));
    });
    return all;
  }, [rooms]);

  const end = useMemo(() => {
    if (screenType === "edit") {
      return <EditButton path={routes.edit.sectionItem.build({ sectionId: sectionID, itemId: _id })} />;
    }

    return (
      <ChatButton
        _id={_id || idFromParams}
        users={allGuests}
        names={chatNames}
        sectionId={SectionId.HOTEL}
        editID={_id || idFromParams}
        joinOnly={joinOnly}
      />
    );
  }, [screenType, _id, idFromParams, allGuests, chatNames, sectionID, joinOnly]);

  const content = (
    <>
      {room && ["personal", "upNext"].includes(screenType) && (
        <HotelRoomInfo
          bookingRef={room.booking_ref}
          extra_docs={room.documents}
          notes={room.comments}
          roomType={room.roomType}
        />
      )}

      {room && ["personal", "upNext"].includes(screenType) && (
        <HotelCheckInTimes checkInTime={room.checkIn} checkOutTime={room.checkOut} />
      )}

      {attachments && (
        <HotelAttachments
          phoneNumber={attachments.phoneNumber}
          notes={attachments.notes}
          extra_docs={attachments.extra_docs}
          website={attachments.website}
          contactName={attachments.contactName}
        />
      )}
      <CategoryList categories={categories} />
      {providerData && <ProviderButton providerData={providerData} />}

      {room && ["personal", "upNext"].includes(screenType) && <HotelGuests rooms={[room]} />}

      {rooms && ["team", "edit", "teamMember"].includes(screenType) && <HotelGuests rooms={rooms} withDate />}
    </>
  );

  if (variant === "static") {
    return (
      <Box x-sp-c="ListKeyPoint">
        <Stack
          direction="column"
          sx={{
            gap: "16px",
          }}
        >
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {mid}
            {end}
          </Stack>

          {content}
        </Stack>
      </Box>
    );
  }

  return (
    <ExpandableSection mid={mid} end={end}>
      {content}
    </ExpandableSection>
  );
};
